import React from "react";
import { Stack, Alert, Box, Divider } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import * as Yup from "yup";
import LockIcon from "@mui/icons-material/Lock";

import FormAlert from "../components/common/FormAlert";
import FormTextField from "../components/common/FormTextField";
import SubmitButton from "../components/buttons/SubmitButton";
import ShiftBreakdown from "../components/shifts/ShiftBreakdown";

import { useAppDispatch } from "../redux/store";
import { closeShift } from "../redux/actions/shiftActions";
import { cleanFormData } from "../helpers/formHelpers";

import { ShiftResponseDto } from "../shared/dtos/shift/ShiftResponseDto";
import { ShiftCloseRequestDto } from "../shared/dtos/shift/ShiftCloseRequestDto";
import { shiftActions } from "../redux/slices/shift";

const closeShiftSchema = Yup.object().shape({
  declaredCash: Yup.number()
    .required("Declared cash is required")
    .min(0, "Cannot be negative"),
});

interface CloseShiftFormProps {
  currentShift: ShiftResponseDto | null;
  loading: boolean;
  error: string | null;
}

const CloseShiftForm: React.FC<CloseShiftFormProps> = ({
  currentShift,
  loading = false,
  error = null,
}) => {
  const dispatch = useAppDispatch();

  if (!currentShift) return null;

  const initialValues = {
    declaredCash: "" as unknown as number,
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormAlert
        message={error}
        severity="error"
        closable
        onClose={() => dispatch(shiftActions.setErrorClose(null))}
      />
      <Box>
        {/* 1. Show the Breakdown */}
        <ShiftBreakdown shift={currentShift} />

        <Divider sx={{ my: 3 }} />

        {/* 2. Show the Form */}
        <Alert severity="info" sx={{ mb: 2 }}>
          Please count the physical cash in your till and enter the exact amount
          below to close the shift.
        </Alert>

        <Formik
          initialValues={initialValues}
          validationSchema={closeShiftSchema}
          onSubmit={(values) => {
            const payload = cleanFormData(values) as ShiftCloseRequestDto;
            dispatch(
              closeShift({
                id: currentShift.id,
                values: payload,
              }),
            );
          }}
        >
          {({ dirty, isValid }: FormikProps<typeof initialValues>) => (
            <Form>
              <Stack spacing={3}>
                <FormTextField
                  name="declaredCash"
                  label="Declared Cash (Counted in Till)"
                  type="number"
                  autoFocus
                />
                <SubmitButton
                  title="Close Shift & Print Z-Report"
                  loading={loading}
                  disabled={!dirty || !isValid || loading}
                  startIcon={<LockIcon />}
                  color="error" // Red button to signify closing
                />
              </Stack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
};

export default CloseShiftForm;
